import { useState } from "react";
import { CommunityPost } from "../components/community/components/post/interfaces/post.interface";

type Params = {
  post?: CommunityPost;
  onClose: () => void;
};

export function useShareActions({ post, onClose }: Params) {
  const [copied, setCopied] = useState(false);

  /**
   * Build the shareable URL for a post.
   */
  const shareUrl = post
    ? `${window.location.origin}/community/posts/${post.id}`
    : window.location.href;

  /**
   * Copy the URL to clipboard + show feedback.
   */
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Failed to copy:", err);
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);

      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        onClose();
      }, 1500);
    }
  };

  /**
   * Use the native Web Share API (mobile).
   */
  const handleNativeShare = async () => {
    if (!navigator.share) return;

    try {
      await navigator.share({
        title: post?.author.name
          ? `Post by ${post.author.name}`
          : "Community Post",
        text: post?.body ?? "",
        url: shareUrl,
      });
      onClose();
    } catch (err) {
      // User cancelled or error — ignore
      if ((err as Error).name !== "AbortError") {
        console.error("Native share failed:", err);
      }
    }
  };

  /**
   * Open a share popup (for external platforms).
   */
  const openSharePopup = (url: string) => {
    window.open(url, "_blank", "width=600,height=500,noopener,noreferrer");
    onClose();
  };

  /**
   * Share to Feed — placeholder.
   */
  const handleShareToFeed = () => {
    // TODO: implement later
    console.log("Share to Feed:", post?.id);
    onClose();
  };

  /**
   * Share to Story — placeholder.
   */
  const handleShareToStory = () => {
    // TODO: implement later
    console.log("Share to Story:", post?.id);
    onClose();
  };

  /**
   * Share to Messenger — placeholder.
   */
  const handleShareToMessenger = () => {
    // TODO: implement later
    console.log("Share to Messenger:", post?.id);
    onClose();
  };

  /**
   * Share on WhatsApp.
   */
  const handleShareToWhatsApp = () => {
    const text = post?.body
      ? `${post.body}\n\n${shareUrl}`
      : shareUrl;
    openSharePopup(
      `https://wa.me/?text=${encodeURIComponent(text)}`
    );
  };

  /**
   * Share on Telegram.
   */
  const handleShareToTelegram = () => {
    const text = post?.body ?? "Check this post";
    openSharePopup(
      `https://t.me/share/url?url=${encodeURIComponent(
        shareUrl
      )}&text=${encodeURIComponent(text)}`
    );
  };

  return {
    copied,
    shareUrl,
    handleCopyLink,
    handleNativeShare,
    handleShareToFeed,
    handleShareToStory,
    handleShareToMessenger,
    handleShareToWhatsApp,
    handleShareToTelegram,
  };
}